import { Box, Grid, Skeleton, Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import SelectionListActionItem from '/@/components/bayer/selection-list-item-actions';
import { useCropProtectionOptions } from '/@/hooks/crop-protection';
import {
    getApplicationOptions,
    SEED_TREATMENT_APPLICATION_MOMENTS_BY_CROP,
    WEED_APPLICATION_MOMENTS_BY_CROP,
} from '/@/store/crop-protection/selectors';
import type { CropProtectionForm } from '/@/types/crop-protection';

const ApplicationMomentStep = ({ form }: { form: UseFormReturn<CropProtectionForm> }) => {
    const options = useMemo(() => getApplicationOptions(), []);
    const { data: filteredOptions, isValidating: isLoading } = useCropProtectionOptions({
        type: form.getValues().type,
        crop: form.getValues().crop,
    });
    useEffect(() => {
        form.reset({
            ...form.getValues(),
            weedType: '',
            weed: '',
            weedSize: '',
            recommendationOption: '',
            diseaseId: '',
            applicationType: '',
            plagueId: '',
        });
    }, [form]);

    const filterConfigByApplicationMoment = (
        options: { applicationMoment: string }[],
        config: Record<string, { title: string; description: string; disabled: boolean; hidden: boolean }>
    ) => {
        const validMoments = new Set(options?.map((option) => option.applicationMoment));
        const filteredConfig = Object.keys(config)
            .filter((key) => validMoments.has(key))
            .reduce(
                (acc, key) => {
                    acc[key] = config[key];
                    return acc;
                },
                {} as Record<string, { title: string; description: string; disabled: boolean }>
            );

        return filteredConfig;
    };

    const filteredConfig = useMemo(() => {
        if (!filteredOptions) return;

        const crop = form.getValues().crop;
        const type = form.getValues().type;
        const allowedMoments =
            type === 'weeds' && crop
                ? WEED_APPLICATION_MOMENTS_BY_CROP[crop]
                : type === 'seed_treatment' && crop
                  ? SEED_TREATMENT_APPLICATION_MOMENTS_BY_CROP[crop]
                  : undefined;

        if (allowedMoments) {
            const apiMoments = new Set(filteredOptions.map((option) => option.applicationMoment));
            return Object.fromEntries(
                allowedMoments
                    .filter((key) => key in options && apiMoments.size > 0 && apiMoments.has(key))
                    .map((key) => [key, options[key]])
            );
        }

        return filterConfigByApplicationMoment(filteredOptions, options);
    }, [filteredOptions, options, form]);

    const handleChange = (key: string) => {
        form.setValue('application', key);
    };
    return (
        <Grid item>
            {!isLoading ? (
                <Stack
                    sx={{
                        display: 'grid',
                        gap: 3,
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gridAutoRows: '1fr',
                    }}
                >
                    {Object.entries(filteredConfig).map(([key, value]) => (
                        <SelectionListActionItem
                            key={key}
                            title={value.title({ crop: form.getValues().crop })}
                            subtitle={value.description({ type: form.getValues().type })}
                            isSelected={form.watch('application') === key}
                            onClick={() => handleChange(key)}
                            disabled={value.disabled({ plantingDate: form.getValues().plantingDate })}
                        />
                    ))}
                </Stack>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    {Array.from(new Array(5)).map((item, index) => (
                        <Skeleton key={index} variant='rectangular' width='100%' height={120} />
                    ))}
                </Box>
            )}
        </Grid>
    );
};

export default ApplicationMomentStep;
